import apiClient from './api-client';

// API Path Constants
const API_BASE = '/api-keys';
const API_PATHS = {
    BASE: API_BASE,
    ME: `${API_BASE}/me`,
    ME_GENERATE: `${API_BASE}/me/generate`,
    ADMIN_ALL: `${API_BASE}/admin/all`,
    ADMIN_CLIENT_GENERATE: (clientId: number) => `${API_BASE}/admin/clients/${clientId}/generate`,
    ADMIN_CLIENT_REVOKE: (clientId: number) => `${API_BASE}/admin/clients/${clientId}`,
} as const;

// Interfaces
export interface ClientApiKey {
    ApiKeyId: number;
    ClientId: number;
    ApiKey: string;
    IsActive: boolean;
    CreatedDate: string;
    CreatedBy: number;
    LastUsedDate?: string;
    RevokedDate?: string;
    RevokedBy?: number;
    RevokedReason?: string;
}

export interface ClientWithApiKey {
    ClientId: number;
    ClientName: string;
    Subdomain: string;
    ApiKey: string;
    IsActive: boolean;
}

export interface ApiKeyResponse {
    success: boolean;
    data: {
        apiKey: string;
        createdDate: string;
        lastUsedDate?: string;
        isActive: boolean;
    };
    message?: string;
}

export interface ApiKeyGenerationResponse {
    success: boolean;
    data: {
        apiKey: string;
        message: string;
    };
}

export interface ApiKeyRevocationResponse {
    success: boolean;
    message: string;
}

export interface AdminApiKeyResponse {
    success: boolean;
    data: {
        clientId: number;
        clientName: string;
        subdomain: string;
        apiKey: string;
        isActive: boolean;
        createdDate: string;
        lastUsedDate?: string;
        revokedDate?: string;
        revokedReason?: string;
    }[];
}

export interface AdminApiKeyGenerationResponse {
    success: boolean;
    data: {
        clientId: number;
        apiKey: string;
        message: string;
    };
}

export interface ApiKeyQueryParams {
    filters?: {
        isActive?: boolean;
        clientId?: number;
    };
    pagination?: {
        page: number;
        pageSize: number;
    };
    sortBy?: 'newest' | 'oldest' | 'clientName' | 'lastUsed';
}

// Service object
export const apiKeysService = {
    // Get current client's API key
    getMyApiKey: async (): Promise<ApiKeyResponse> => {
        try {
            const response = await apiClient.get<ApiKeyResponse>(API_PATHS.ME);
            
            if (!response.success) {
                throw new Error('Failed to fetch API key');
            }

            return response;
        } catch (error) {
            console.error('Error fetching API key:', error);
            throw error;
        }
    },

    // Generate new API key for current client
    generateNewApiKey: async (): Promise<ApiKeyGenerationResponse> => {
        try {
            const response = await apiClient.post<ApiKeyGenerationResponse>(
                API_PATHS.ME_GENERATE,
                {}
            );

            if (!response.success) {
                throw new Error('Failed to generate new API key');
            }

            return response;
        } catch (error) {
            console.error('Error generating new API key:', error);
            throw error;
        }
    },

    // Revoke current client's API key
    revokeApiKey: async (reason?: string): Promise<ApiKeyRevocationResponse> => {
        try {
            const payload = reason ? { reason } : {};
            const response = await apiClient.delete<ApiKeyRevocationResponse>(
                API_PATHS.ME,
                { data: payload }
            );

            if (!response.success) {
                throw new Error('Failed to revoke API key');
            }

            return response;
        } catch (error) {
            console.error('Error revoking API key:', error);
            throw error;
        }
    },

    // Admin: Get all API keys (super admin only)
    getAllApiKeys: async (queryParams?: ApiKeyQueryParams): Promise<AdminApiKeyResponse> => {
        try {
            const params = new URLSearchParams();
            
            if (queryParams?.filters) {
                if (queryParams.filters.isActive !== undefined) {
                    params.append('isActive', queryParams.filters.isActive.toString());
                }
                if (queryParams.filters.clientId) {
                    params.append('clientId', queryParams.filters.clientId.toString());
                }
            }

            if (queryParams?.pagination) {
                params.append('page', queryParams.pagination.page.toString());
                params.append('pageSize', queryParams.pagination.pageSize.toString());
            }

            if (queryParams?.sortBy) {
                params.append('sortBy', queryParams.sortBy);
            }

            const url = params.toString() ? `${API_PATHS.ADMIN_ALL}?${params.toString()}` : API_PATHS.ADMIN_ALL;
            const response = await apiClient.get<AdminApiKeyResponse>(url);

            if (!response.success) {
                throw new Error('Failed to fetch all API keys');
            }

            return response;
        } catch (error) {
            console.error('Error fetching all API keys:', error);
            throw error;
        }
    },

    // Admin: Generate API key for specific client (super admin only)
    generateApiKeyForClient: async (clientId: number): Promise<AdminApiKeyGenerationResponse> => {
        try {
            const response = await apiClient.post<AdminApiKeyGenerationResponse>(
                API_PATHS.ADMIN_CLIENT_GENERATE(clientId),
                {}
            );

            if (!response.success) {
                throw new Error('Failed to generate API key for client');
            }

            return response;
        } catch (error) {
            console.error('Error generating API key for client:', error);
            throw error;
        }
    },

    // Admin: Revoke API key for specific client (super admin only)
    revokeApiKeyForClient: async (clientId: number, reason?: string): Promise<ApiKeyRevocationResponse> => {
        try {
            const payload = reason ? { reason } : {};
            const response = await apiClient.delete<ApiKeyRevocationResponse>(
                API_PATHS.ADMIN_CLIENT_REVOKE(clientId),
                { data: payload }
            );

            if (!response.success) {
                throw new Error('Failed to revoke API key for client');
            }

            return response;
        } catch (error) {
            console.error('Error revoking API key for client:', error);
            throw error;
        }
    },

    // Utility function to validate API key format
    validateApiKeyFormat: (apiKey: string): boolean => {
        // Check if API key follows the expected format: kav_[number]_[number]_[number]
        const apiKeyPattern = /^kav_\d+_\d+_\d+$/;
        return apiKeyPattern.test(apiKey);
    },

    // Utility function to mask API key for display
    maskApiKey: (apiKey: string, showChars = 8): string => {
        if (!apiKey || apiKey.length <= showChars) {
            return apiKey;
        }
        
        const visiblePart = apiKey.substring(0, showChars);
        const maskedPart = '*'.repeat(apiKey.length - showChars);
        return `${visiblePart}${maskedPart}`;
    },

    // Utility function to format API key for display
    formatApiKeyForDisplay: (apiKey: string, showFull = false): string => {
        if (!apiKey) {
            return '';
        }

        if (showFull) {
            return apiKey;
        }

        return apiKeysService.maskApiKey(apiKey);
    }
};


